<template>
  <v-dialog
    v-model="internalIsOpen"
    max-width="600"
    persistent
  >
    <v-card>
      <v-card-title class="headline">
        {{ uploadStage === 'upload' ? 'Kollektenplan hochladen' : 'Import vorbereitet' }}
      </v-card-title>

      <v-card-text>
        <!-- Upload Stage -->
        <div v-if="uploadStage === 'upload'">
          <p>
            Bitte wählen Sie eine Excel-Datei mit Ihrem Kollektenplan aus.<br>
            Bitte nutzen Sie ausschließlich unsere <a @click="downloadCollectionPlanTemplate()">Vorlagen</a>, alle anderen Dateien sind nicht kompatibel.
          </p>

          <v-file-input
            v-model="selectedFile"
            label="Excel-Datei auswählen"
            accept=".xlsx,.xls"
            prepend-icon="mdi-microsoft-excel"
            show-size
            :disabled="isUploading"
            :error-messages="uploadError"
            @change="uploadError = null"
          ></v-file-input>

          <v-alert
            v-if="uploadError"
            type="error"
            dense
            class="mt-3"
          >
            {{ uploadError }}
          </v-alert>
        </div>

        <!-- Review Stage -->
        <div v-else-if="uploadStage === 'review'">
          <p>Die Datei wurde erfolgreich hochgeladen und ist bereit für den Import.</p>

          <v-list dense>
            <v-list-item>
              <v-list-item-icon>
                <v-icon>mdi-file-excel</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Datei</v-list-item-title>
                <v-list-item-subtitle>{{ uploadedFileName }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon>mdi-domain</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Organisation</v-list-item-title>
                <v-list-item-subtitle>{{ organization.name }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon>
                <v-icon>mdi-information</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Status</v-list-item-title>
                <v-list-item-subtitle>{{ uploadStateLabel }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-alert
            type="info"
            color="primary"
            dense
            class="mt-3"
          >
            Klicken Sie auf "Weiter", um den Import zu starten.
          </v-alert>
        </div>

        <!-- Import in progress -->
        <div v-else-if="uploadStage === 'importing'">
          <v-progress-linear
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-linear>
          <p class="text-center">Import läuft...</p>
        </div>

        <!-- Success Stage -->
        <div v-else-if="uploadStage === 'success'">
          <v-alert
            type="success"
            prominent
          >
            <v-row align="center">
              <v-col class="grow">
                Der Import wurde erfolgreich abgeschlossen!
              </v-col>
            </v-row>
          </v-alert>
        </div>

        <!-- Error Stage -->
        <div v-else-if="uploadStage === 'error'">
          <v-alert
            type="error"
            prominent
          >
            <v-row align="center">
              <v-col class="grow">
                <div class="mb-2"><strong>Der Import ist fehlgeschlagen</strong></div>
                <div v-if="importError">{{ importError }}</div>
              </v-col>
            </v-row>
          </v-alert>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>

        <!-- Upload stage buttons -->
        <template v-if="uploadStage === 'upload'">
          <v-btn
            text
            @click="closeDialog"
            :disabled="isUploading"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            @click="uploadFile"
            :disabled="!selectedFile || isUploading"
            :loading="isUploading"
          >
            Hochladen
          </v-btn>
        </template>

        <!-- Review stage buttons -->
        <template v-else-if="uploadStage === 'review'">
          <v-btn
            text
            @click="discardUpload"
            :disabled="isImporting"
          >
            Verwerfen
          </v-btn>
          <v-btn
            color="primary"
            @click="startImport"
            :disabled="isImporting"
            :loading="isImporting"
          >
            Weiter
          </v-btn>
        </template>

        <!-- Importing stage buttons -->
        <template v-else-if="uploadStage === 'importing'">
          <v-btn
            text
            disabled
          >
            Bitte warten...
          </v-btn>
        </template>

        <!-- Success/Error stage buttons -->
        <template v-else-if="uploadStage === 'success' || uploadStage === 'error'">
          <v-btn
            color="primary"
            @click="closeAndRefresh"
          >
            Schließen
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { downloadBlob } from "@/lib/file-tools"

export default {
  name: 'CollectionPlanUploadDialog',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    organization: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      uploadStage: 'upload', // upload, review, importing, success, error
      selectedFile: null,
      uploadedFileName: '',
      isUploading: false,
      isImporting: false,
      uploadError: null,
      importError: null,
      uploadId: null,
      uploadState: null
    }
  },
  computed: {
    internalIsOpen: {
      get () {
        return this.isOpen
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    uploadStateLabel () {
      if (!this.uploadState) return 'Bereit'
      const labels = {
        uploaded: 'Hochgeladen',
        parametrised: 'Bereit zum Import',
        started: 'Import läuft',
        finished: 'Abgeschlossen',
        error: 'Fehler'
      }
      return labels[this.uploadState] || this.uploadState
    }
  },
  methods: {
    async downloadCollectionPlanTemplate (year) {
      const url = new URL(`${this.organization.url}kollektenplan_template/`)
      if (year) {
        url.searchParams.set('with_dates', year)
      }

      const response = await this.$store.getters.restApi(url.toString(), {
        responseType: 'blob'
      })
      const contentType = response.headers['content-type']
      const filename = `${this.organization.id}-${this.organization.name}-kollektenplan.xlsx`
      downloadBlob(response.data, filename, contentType)
    },
    async uploadFile () {
      if (!this.selectedFile) {
        return
      }

      this.isUploading = true
      this.uploadError = null

      try {
        const formData = new FormData()
        formData.append('data_file', this.selectedFile)
        formData.append('organization', this.organization.id)

        const response = await this.$store.getters.restApi.post('collection_plan_upload/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.uploadId = response.data.id || response.data.url
        this.uploadState = response.data.state
        this.uploadedFileName = this.selectedFile.name
        this.uploadStage = 'review'
      } catch (error) {
        this.uploadError = error.response?.data?.detail ||
                          error.response?.data?.data_file?.[0] ||
                          'Fehler beim Hochladen der Datei'
      } finally {
        this.isUploading = false
      }
    },

    async startImport () {
      this.isImporting = true
      this.importError = null
      this.uploadStage = 'importing'

      try {
        const endpoint = `collection_plan_upload/${this.uploadId}/start/`

        const response = await this.$store.getters.restApi.post(endpoint)

        this.uploadState = response.data.state

        this.uploadStage = 'success'
      } catch (error) {
        this.uploadStage = 'error'
        this.importError = error.response?.data?.error ||
                          error.response?.data?.detail ||
                          'Fehler beim Import'
      } finally {
        this.isImporting = false
      }
    },

    discardUpload () {
      // Simply reset to upload stage
      this.resetDialog()
    },

    closeDialog () {
      if (this.uploadStage === 'success') {
        this.$emit('import-success')
      }
      this.$emit('close')
      this.resetDialog()
    },

    closeAndRefresh () {
      if (this.uploadStage === 'success') {
        this.$emit('import-success')
      }
      this.closeDialog()
    },

    resetDialog () {
      this.uploadStage = 'upload'
      this.selectedFile = null
      this.uploadedFileName = ''
      this.uploadError = null
      this.importError = null
      this.uploadId = null
      this.uploadState = null
    }
  },
  watch: {
    isOpen (newVal) {
      if (!newVal) {
        // Reset dialog when closed
        this.resetDialog()
      }
    }
  }
}
</script>

<style scoped>
.headline {
  color: var(--v-primary-base);
}
</style>
